/**
 * Mechagora desk tokens — brand presence + ui-ux-pro-max system contract.
 *
 * Presence rules:
 * - Cool ice / steel own the chrome (primary, focus, links) for long trading sessions.
 * - Bull/bear are functional market colors only — never brand metal, never “tech blue UI”.
 * - Guideline packs (Midnight / Cream) stay cool for surfaces and chrome. No peach metal.
 * - Accessible Signals: blue/amber for *sides only*; primary chrome stays cool ice.
 *
 * Pro-max (skill) — entire ThemeSystem must honor:
 * - Primary text ≥4.5:1 on container; secondary ≥3:1; metal on-primary = dark ink.
 * - Focus ring = primary metal; never strip outlines without :focus-visible replacement.
 * - Density dial 9/10: clear L0/L1/L2 elevation; hairlines for zone scan.
 * - Motion 150–300ms; reduced-motion → 0 (see pro-max.foundation.css).
 * - Touch targets ≥44px at control layer (buttons.desk.css + foundation).
 * - Color alone is never P&L meaning — pair with +/− / labels (trading UX).
 *
 * Trading practice: no pure #000/#FFF canvas; matte text; brand ≠ P&L.
 */

/**
 * Night — brand espresso desk (trading-viable contrast inside the warm stream).
 *
 * Goals for dense book / P&L / mono meta:
 * - Clear L0→L1→L2 elevation steps (bay / panel / chrome) without leaving chocolate.
 * - Primary text near-parchment (matte, not pure #FFF) for long-session scan.
 * - Secondary lifted for mono labels (still warm taupe).
 * - Grid hairlines strong enough to zone instruments (~2.5:1+ on panels).
 * - Bull/bear true green/red with high luminance on espresso (not teal/coral).
 * - Ice metal readable as focus/chrome — not peach, not lemon.
 */
/** Pre-G2 espresso values, kept only for Accessible Signals dark (Night G2 r14). */
const ESPRESSO_DARK_LEGACY = {
  /** Deep bay — not pure black (OLED smear / void) */
  canvas: '#0E0B09',
  /** Panel body — one clear step above bay */
  container: '#181818',
  /** Chrome / float / navbar — readable step above panel */
  elevated: '#3A2E26',
  /** High-scan parchment ink */
  textPrimary: '#FBF7F2',
  /** Meta / secondary — AA-friendly on panels */
  textSecondary: '#B8B8B8',
  /** Zone hairlines */
  grid: '#6B5448',
  /** Lock success — forest, with signed text (never mint) */
  bull: '#3D7A5C',
  /** Lock loss — soft red, with signed text */
  bear: '#C45C5C',
  /**
   * Ice steel — high-contrast chrome for long sessions.
   * Not peach, champagne, or rose-gold.
   */
  primary: '#5B9CF6',
  primaryRgb: '196, 223, 240',
  warning: '#E3B341',
  info: '#B8B8B8',
} as const

/** Night G2 Graphite Pro (r14, Elena): slot-by-slot map to the G2 first-paint pack. */
export const MECHAGORA_DARK = {
  canvas: '#0F0F0F',
  container: '#181818',
  elevated: '#212121',
  textPrimary: '#DBDBDB',
  textSecondary: '#B8B8B8',
  grid: '#3E3E3E',
  bull: '#089981',
  bear: '#F23645',
  primary: '#5B9CF6',
  primaryRgb: '91, 156, 246',
  warning: '#FFB74D',
  info: '#B8B8B8',
} as const

/**
 * Day — sand bay + warm ivory (trading-viable contrast inside the warm stream).
 *
 * Goals:
 * - Sand well deep enough that ivory panels lift cleanly (zone scan).
 * - Espresso ink near-black for prices; secondary darker for 12px mono.
 * - Grid rules strong enough for table denseness on paper.
 * - Rose-gold metal deep enough for AA on ivory (links / focus).
 * - Bull/bear deeper greens/reds for AA on light paper (still true sides).
 */
export const MECHAGORA_LIGHT = {
  /** Sand instrument bay */
  canvas: '#D6C4AF',
  /** Warm ivory panels (not cold grey-white) */
  container: '#FFF9F4',
  /** Brightest float */
  elevated: '#FFFFFF',
  /** Espresso ink — ≥4.5:1 on sand + ivory */
  textPrimary: '#120E0B',
  /**
   * Secondary meta — AA on sand bay (~8:1) and ivory (~13:1).
   * Do not lighten: sand is already mid-luminance.
   */
  textSecondary: '#3A2A22',
  /** Table / rack hairlines */
  grid: '#9A7A5C',
  /** True deep green on paper */
  bull: '#15803D',
  /** True deep red on paper */
  bear: '#B91C1C',
  /**
   * Daylight steel — readable on ivory, not terracotta/peach.
   */
  primary: '#274E63',
  primaryRgb: '39, 78, 99',
  warning: '#B8860B',
  info: '#5C4036',
} as const

/**
 * Light text ladder (pro-max AA on sand #D6C4AF + ivory #FFF9F4).
 * Quaternary must stay ≥4.5:1 on canvas — #8A7366 fails (~2.6:1).
 */
export const MECHAGORA_LIGHT_TEXT = {
  primary: '#120E0B',
  secondary: '#3A2A22',
  /** Same family as secondary — still AA on sand */
  tertiary: '#4A3A32',
  /** Darkest “muted” that still passes AA on sand (~4.9:1) */
  quaternary: '#5C4A40',
  disabled: '#5C4A40',
} as const

/**
 * Night HFT surfaces — cool charcoal bay.
 * Primary is cool ice for long-session chrome.
 */
/** Night HFT — cool charcoal; chrome is ice, not peach. */
export const MIDNIGHT_SLATE = {
  canvas: '#0F1219',
  container: '#161A25',
  elevated: '#1E2433',
  textPrimary: '#F1F3F7',
  /** ≥3:1 secondary on panel */
  textSecondary: '#B4BAC8',
  /** Stronger hairlines for zone scan */
  grid: '#2A3144',
  bull: '#34D399',
  bear: '#F87171',
  primary: '#5B9CF6',
  primaryRgb: '196, 223, 240',
  warning: '#E3B341',
  info: '#B4BAC8',
} as const

/**
 * Day paper — cool paper surfaces; rose-gold metal; AA text/meta.
 */
export const CREAM_PAPER = {
  canvas: '#E8EAF0',
  container: '#FAFAFC',
  elevated: '#FFFFFF',
  textPrimary: '#141824',
  textSecondary: '#4A5163',
  grid: '#C8CDD8',
  bull: '#15803D',
  bear: '#B91C1C',
  primary: '#274E63',
  primaryRgb: '39, 78, 99',
  warning: '#8A6A08',
  info: '#4A5163',
} as const

/**
 * Accessibility — sides only (not red/green). Chrome stays ice; sides stay semantic.
 * Pair with +/− / Buy/Sell labels.
 */
export const ACCESSIBLE_SIGNALS = {
  darkBull: '#3B82F6',
  darkBear: '#F59E0B',
  lightBull: '#2563EB',
  lightBear: '#D97706',
} as const

/** Editable custom desk token set (Theme Studio). */
export interface DeskTokenSet {
  canvas: string
  container: string
  elevated: string
  textPrimary: string
  textSecondary: string
  grid: string
  bull: string
  bear: string
  primary: string
}

export function hexToRgbTriplet(hex: string): string {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const n = parseInt(full, 16)
  if (Number.isNaN(n)) return '0, 0, 0'
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

function toDesk(t: {
  canvas: string
  container: string
  elevated: string
  textPrimary: string
  textSecondary: string
  grid: string
  bull: string
  bear: string
  primary: string
}): DeskTokenSet {
  return {
    canvas: t.canvas,
    container: t.container,
    elevated: t.elevated,
    textPrimary: t.textPrimary,
    textSecondary: t.textSecondary,
    grid: t.grid,
    bull: t.bull,
    bear: t.bear,
    primary: t.primary,
  }
}

export function tokensFromMechagora(variant: 'light' | 'dark' = 'dark'): DeskTokenSet {
  return toDesk(variant === 'light' ? MECHAGORA_LIGHT : MECHAGORA_DARK)
}

export function tokensFromMidnight(): DeskTokenSet {
  return toDesk(MIDNIGHT_SLATE)
}

export function tokensFromCream(): DeskTokenSet {
  return toDesk(CREAM_PAPER)
}

/**
 * Accessible sides on brand-warm shells so chrome stays Mechagora presence:
 * dark → espresso surfaces + ice primary + a11y sides
 * light → sand/ivory + steel primary + a11y sides
 */
export function tokensFromAccessible(variant: 'light' | 'dark' = 'dark'): DeskTokenSet {
  if (variant === 'light') {
    return {
      ...tokensFromMechagora('light'),
      bull: ACCESSIBLE_SIGNALS.lightBull,
      bear: ACCESSIBLE_SIGNALS.lightBear,
    }
  }
  return {
    ...toDesk(ESPRESSO_DARK_LEGACY),
    bull: ACCESSIBLE_SIGNALS.darkBull,
    bear: ACCESSIBLE_SIGNALS.darkBear,
  }
}

/** Night G2 (Graphite Pro + Sand): mirror of the --nt-* block in tokens.desk.css.
 *  Scoped to Night on /trading. Keep in sync with spec-G2 BUILD.md s1. */
export const NIGHT_G2 = {
  surfaceCanvas: '#0F0F0F',
  surfaceWell: '#0F0F0F',
  surfacePanel: '#181818',
  surfaceRaised: '#1C1C1C',
  surfaceElevated: '#212121',
  surfaceHover: '#262626',
  surfaceSelected: '#2A2A2A',
  borderPanel: '#3E3E3E',
  divider: '#262626',
  borderControl: '#757575',
  chartGrid: '#1A1A1A',
  textStrong: '#F2F2F2',
  textPrimary: '#DBDBDB',
  textSecondary: '#B8B8B8',
  textMuted: '#929292',
  buy: '#089981',
  sell: '#F23645',
  buyText: '#22AB94',
  sellText: '#F75C68',
  accent: '#2962FF',
  accentText: '#5B9CF6',
  linkHover: '#89B8F9',
  warningText: '#FFB74D',
  statusOk: '#3FB950',
  sand: '#C8BCA9',
  sandDim: '#847967',
  crosshair: '#707070',
  crosshairLabelBg: '#2A2A2A',
} as const
